import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Dropdown, Button, Drawer, message, Badge } from 'antd';
import { UserOutlined, MenuOutlined, CloseOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import Auth from '../auth/auth.js';
import Account from '../account/account';
import { userLoaded } from '../../redux/actions';
import { useWindowWidth } from '@react-hook/window-size';
import { serverFetch } from '../../server';
import './nav-menu.css';
import { getIsVkSession } from '../../extensions/utils.js';

function getItem(label, key) {
    return { key, label };
}

function getNavKeyFromPath(pathname) {
    if (pathname === '/' || pathname.startsWith('/home')) return 'home';
    if (pathname.startsWith('/catalog')) return 'catalog';
    if (pathname.startsWith('/calendar')) return 'calendar';
    if (pathname.startsWith('/purchases')) return 'purchases';
    if (pathname.startsWith('/history')) return 'history';
    return '';
}

const NavMenu = () => {
    const d = useDispatch();
    const location = useLocation();
    const current = getNavKeyFromPath(location.pathname);
    const user = useSelector((s) => s.user);
    const onlyWidth = useWindowWidth();
    const [navItems, setNavItems] = useState([]);
    const [showSignin, setShowSignin] = useState(false);
    const [showAccountInfo, setShowAccountInfo] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const company = useSelector((s) => s.company);
    const cartCount = useSelector((s) => (s.cart?.items || []).filter((i) => i.companyId === company?.id).length);
    const isMobile = onlyWidth <= 744;
    const isVkSession = getIsVkSession();

    const logout = () => {
        serverFetch(`account/logout`, { method: 'POST' }, user)
            .then(() => {
                d(userLoaded());
            })
            .catch(() => {
                message.error('Ошибка выхода из системы');
            });
    };

    useEffect(() => {
        let tempItems = [
            getItem(<Link to='/home'>Главная</Link>, 'home'),
            getItem(<Link to='/catalog'>Услуги</Link>, 'catalog'),
        ];
        if (company && company.hasSchedule) tempItems.push(getItem(<Link to='/calendar'>Расписание</Link>, 'calendar'));
        if (user || !isVkSession) tempItems.push(getItem(<Link to='/purchases'>Покупки</Link>, 'purchases'));
        if (company && company.hasHistory && (user || !isVkSession)) tempItems.push(getItem(<Link to='/history'>История</Link>, 'history'));
        setNavItems(tempItems);
    }, [user, company, isVkSession]);

    useEffect(() => {
        if (!isMobile) setMobileMenuOpen(false);
    }, [isMobile]);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const handleClick = () => {
        setMobileMenuOpen(false);
    };

    const accountItems = [
        {
            key: '1',
            label: (
                <Button type='link' className='account-item-btn' onClick={() => setShowAccountInfo(true)} size='middle'>
                    Мои данные
                </Button>
            ),
        },
    ];

    if (!isVkSession)
        accountItems.push({
            key: '2',
            label: (
                <Button type='link' className='account-item-btn' onClick={() => logout()} size='middle'>
                    Выход
                </Button>
            ),
        });

    const authControl = user ? (
        <Dropdown menu={{ items: accountItems }} placement='bottomRight'>
            <div className='account-btn nav-menu-account-btn'>
                <UserOutlined />
                <span>{!isMobile && (user.userName || user.userLogin)}</span>
            </div>
        </Dropdown>
    ) : (
        isVkSession || (
            <Button type='primary' className='login-btn' onClick={() => setShowSignin(true)} size='middle'>
                Вход
            </Button>
        )
    );

    const cartControl = cartCount > 0 ? (
        <Link to='/cart' className='nav-menu-cart-btn' aria-label='Корзина'>
            <Badge count={cartCount} size='small' offset={[2, -2]}>
                <ShoppingCartOutlined />
            </Badge>
        </Link>
    ) : null;

    const selectedKeys = current ? [current] : [];
    const companyPhone = company?.phone?.trim();
    const companyAddress = company?.address?.trim();
    const hasContacts = Boolean(companyPhone || companyAddress);
    const phoneHref = companyPhone ? `tel:${companyPhone.replace(/[^\d+]/g, '')}` : null;

    const contactsBlock = hasContacts ? (
        <div className='nav-menu-contacts'>
            {companyPhone && (
                <a className='nav-menu-contacts-phone' href={phoneHref}>
                    {companyPhone}
                </a>
            )}
            {companyAddress && <div className='nav-menu-contacts-address'>{companyAddress}</div>}
        </div>
    ) : null;

    return (
        <>
            <div className={`nav-menu-container${isMobile ? ' nav-menu-container--mobile' : ''}`}>
                {!isMobile && (
                    <Menu
                        className='nav-menu'
                        key='menu'
                        onClick={handleClick}
                        selectedKeys={selectedKeys}
                        mode='horizontal'
                        theme='light'
                        disabledOverflow={true}
                        items={navItems}
                    />
                )}
                <div className='nav-menu-actions'>
                    {!isMobile && contactsBlock}
                    {cartControl}
                    {authControl}
                    {isMobile && (
                        <button
                            type='button'
                            className='nav-burger-btn'
                            aria-label='Открыть меню'
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <MenuOutlined />
                        </button>
                    )}
                </div>
            </div>
            <Drawer
                className='nav-mobile-drawer'
                placement='right'
                open={isMobile && mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                width='78%'
                closable={false}
                title={
                    <div className='nav-mobile-drawer-title'>
                        <button
                            type='button'
                            className='nav-mobile-drawer-close'
                            aria-label='Закрыть меню'
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <CloseOutlined />
                        </button>
                        <span>Навигация</span>
                    </div>
                }
            >
                <Menu
                    className='nav-menu--drawer'
                    onClick={handleClick}
                    selectedKeys={selectedKeys}
                    mode='vertical'
                    theme='light'
                    items={navItems}
                />
                {hasContacts && (
                    <div className='nav-mobile-contacts'>
                        {companyPhone && (
                            <a className='nav-mobile-contacts-phone' href={phoneHref}>
                                {companyPhone}
                            </a>
                        )}
                        {companyAddress && <div className='nav-mobile-contacts-address'>{companyAddress}</div>}
                    </div>
                )}
            </Drawer>
            {showSignin && <Auth onClose={() => setShowSignin(false)} />}
            {showAccountInfo && <Account open={showAccountInfo} onClose={() => setShowAccountInfo(false)} />}
        </>
    );
};

export default NavMenu;
